import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  FolderOpen, Download, Trash2, RefreshCw, Mail, Eye, RotateCcw, ArrowLeft, FileText, FileSpreadsheet, Inbox,
} from "lucide-react";

const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round((n || 0) / 1024))} Ko`);
const fmtDate = (s) => { try { return new Date(s).toLocaleString("fr-FR"); } catch { return s || ""; } };

export const ArchivesCenter = () => {
  const [view, setView] = useState("home"); // home | folder | files | trash
  const [overview, setOverview] = useState({ folders: [], trash_count: 0, files_count: 0 });
  const [folder, setFolder] = useState(null); // {folder,label,columns,records}
  const [files, setFiles] = useState([]);
  const [trash, setTrash] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const loadOverview = useCallback(async () => {
    try { const { data } = await api.get("/admin/archives/folders"); setOverview(data); }
    catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const openFolder = async (id, query = "") => {
    setBusy(true);
    try { const { data } = await api.get(`/admin/archives/folder/${id}`, { params: { q: query } }); setFolder(data); setView("folder"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const delRecord = async (rid) => {
    if (!folder || !window.confirm("Envoyer cet élément à la corbeille ?")) return;
    try { await api.delete(`/admin/archives/folder/${folder.folder}/${rid}`); toast.success("Déplacé vers la corbeille."); openFolder(folder.folder, q); loadOverview(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const exportFolder = async (id, format) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/archives/folder/${id}/export`, { format });
      toast.success(`Fichier ${format.toUpperCase()} généré (${data.count} élément(s)). Retrouvez-le dans « Fichiers générés ».`);
      loadOverview(); await openFiles();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const openFiles = async () => {
    try { const { data } = await api.get("/admin/archives/files"); setFiles(data.items || []); setView("files"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openTrash = async () => {
    try { const { data } = await api.get("/admin/archives/trash"); setTrash(data.items || []); setView("trash"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const blobFor = async (f) => {
    const r = await api.get(`/admin/archives/files/${f.id}/download`, { responseType: "blob" });
    return window.URL.createObjectURL(r.data);
  };
  const consultFile = async (f) => {
    try { const url = await blobFor(f); window.open(url, "_blank"); setTimeout(() => window.URL.revokeObjectURL(url), 60000); }
    catch (e) { toast.error(apiError(e)); }
  };
  const downloadFile = async (f) => {
    try { const url = await blobFor(f); const a = document.createElement("a"); a.href = url; a.download = f.filename; a.click(); setTimeout(() => window.URL.revokeObjectURL(url), 60000); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendFile = async (f) => {
    const email = window.prompt("Envoyer ce fichier par e-mail à l'adresse :");
    if (!email) return;
    try { await api.post(`/admin/archives/files/${f.id}/send`, { email }); toast.success(`Envoyé à ${email} ✅`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteFile = async (f) => {
    if (!window.confirm("Supprimer définitivement ce fichier généré ?")) return;
    try { await api.delete(`/admin/archives/files/${f.id}`); toast.success("Fichier supprimé."); openFiles(); loadOverview(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const restoreTrash = async (t) => {
    try { await api.post(`/admin/archives/trash/${t.id}/restore`); toast.success("Élément restauré."); openTrash(); loadOverview(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const purgeTrash = async (t) => {
    if (!window.confirm("Suppression DÉFINITIVE. Cet élément sera perdu pour toujours. Continuer ?")) return;
    try { await api.delete(`/admin/archives/trash/${t.id}`); toast.success("Supprimé définitivement."); openTrash(); loadOverview(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const card = "bg-white border border-[#ECE7DE] rounded-2xl p-4 shadow-[0_2px_16px_rgba(17,24,20,0.04)]";

  return (
    <div data-testid="archives-center" className="space-y-5">
      <div className="flex items-center gap-2">
        <FolderOpen className="w-5 h-5 text-[#D94F2B]" />
        <h2 className="font-head font-black text-xl">Centre d'archives</h2>
      </div>

      {view === "home" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(overview.folders || []).map((f) => (
              <button key={f.id} data-testid={`archive-folder-${f.id}`} onClick={() => openFolder(f.id)}
                className={`${card} text-left hover:-translate-y-0.5 transition-transform`}>
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#FEF1E7] text-[#D8531E]"><FolderOpen className="w-5 h-5" /></span>
                  <div>
                    <div className="font-bold text-[#1A1A1A]">{f.label}</div>
                    <div className="text-sm text-[#75807A]">{f.count} élément(s)</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button data-testid="archive-open-files" onClick={openFiles} className={`${card} text-left hover:-translate-y-0.5 transition-transform`}>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#E8F3EC] text-[#0B7A3B]"><Download className="w-5 h-5" /></span>
                <div><div className="font-bold">Fichiers générés</div><div className="text-sm text-[#75807A]">{overview.files_count} fichier(s) · consulter / télécharger / envoyer</div></div>
              </div>
            </button>
            <button data-testid="archive-open-trash" onClick={openTrash} className={`${card} text-left hover:-translate-y-0.5 transition-transform`}>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#FBE9E7] text-[#C62828]"><Trash2 className="w-5 h-5" /></span>
                <div><div className="font-bold">Corbeille</div><div className="text-sm text-[#75807A]">{overview.trash_count} élément(s) · restaurer / supprimer définitivement</div></div>
              </div>
            </button>
          </div>
        </>
      )}

      {view === "folder" && folder && (
        <div className={card}>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Button data-testid="archive-back" variant="outline" onClick={() => { setView("home"); setQ(""); loadOverview(); }} className="h-9"><ArrowLeft className="w-4 h-4 mr-1" /> Dossiers</Button>
            <div className="font-black text-lg flex-1 min-w-[140px]">{folder.label} <span className="text-[#75807A] font-medium text-sm">({folder.records.length})</span></div>
            <div className="flex items-center gap-2">
              <Input data-testid="archive-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && openFolder(folder.folder, q)} placeholder="Rechercher…" className="h-9 w-40" />
              <Button data-testid="archive-export-pdf" disabled={busy} onClick={() => exportFolder(folder.folder, "pdf")} className="h-9 bg-[#D94F2B] hover:opacity-90 text-white"><FileText className="w-4 h-4 mr-1" /> PDF</Button>
              <Button data-testid="archive-export-csv" disabled={busy} onClick={() => exportFolder(folder.folder, "csv")} className="h-9 bg-[#0B7A3B] hover:opacity-90 text-white"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#75807A] border-b border-[#ECE7DE]">
                {folder.columns.map((c) => <th key={c.key} className="py-2 pr-3 font-semibold whitespace-nowrap">{c.label}</th>)}
                <th className="py-2 pr-2 text-right">Action</th>
              </tr></thead>
              <tbody>
                {folder.records.length === 0 && <tr><td colSpan={folder.columns.length + 1} className="py-6 text-center text-[#75807A]">Aucun élément.</td></tr>}
                {folder.records.map((r) => (
                  <tr key={r.id} data-testid={`archive-row-${r.id}`} className="border-b border-[#F2EEE7] hover:bg-[#FBF6EF]">
                    {folder.columns.map((c) => <td key={c.key} className="py-2 pr-3 align-top">{r[c.key]}</td>)}
                    <td className="py-2 pr-2 text-right">
                      <button data-testid={`archive-del-${r.id}`} onClick={() => delRecord(r.id)} className="inline-flex items-center gap-1 text-[#C62828] hover:underline"><Trash2 className="w-4 h-4" /> Supprimer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "files" && (
        <div className={card}>
          <div className="flex items-center gap-2 mb-4">
            <Button data-testid="archive-back-files" variant="outline" onClick={() => { setView("home"); loadOverview(); }} className="h-9"><ArrowLeft className="w-4 h-4 mr-1" /> Dossiers</Button>
            <div className="font-black text-lg flex-1">Fichiers générés ({files.length})</div>
            <Button variant="outline" className="h-9" onClick={openFiles}><RefreshCw className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-2">
            {files.length === 0 && <div className="py-6 text-center text-[#75807A] flex flex-col items-center gap-2"><Inbox className="w-6 h-6" /> Aucun fichier généré. Ouvrez un dossier puis « PDF » ou « Excel ».</div>}
            {files.map((f) => (
              <div key={f.id} data-testid={`archive-file-${f.id}`} className="flex flex-wrap items-center gap-2 border border-[#ECE7DE] rounded-xl p-3">
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${f.format === "pdf" ? "bg-[#FEE9E2] text-[#D8531E]" : "bg-[#E8F3EC] text-[#0B7A3B]"}`}>{f.format.toUpperCase()}</span>
                <div className="flex-1 min-w-[160px]">
                  <div className="font-semibold truncate">{f.label} · {f.count} élément(s)</div>
                  <div className="text-xs text-[#75807A]">{f.filename} · {fmtSize(f.size)} · {fmtDate(f.created_at)}</div>
                </div>
                <button data-testid={`archive-file-view-${f.id}`} onClick={() => consultFile(f)} className="inline-flex items-center gap-1 text-sm text-[#1A1A1A] hover:underline"><Eye className="w-4 h-4" /> Consulter</button>
                <button data-testid={`archive-file-download-${f.id}`} onClick={() => downloadFile(f)} className="inline-flex items-center gap-1 text-sm text-[#1A1A1A] hover:underline"><Download className="w-4 h-4" /> Télécharger</button>
                <button data-testid={`archive-file-send-${f.id}`} onClick={() => sendFile(f)} className="inline-flex items-center gap-1 text-sm text-[#0B7A3B] hover:underline"><Mail className="w-4 h-4" /> Envoyer</button>
                <button data-testid={`archive-file-del-${f.id}`} onClick={() => deleteFile(f)} className="inline-flex items-center gap-1 text-sm text-[#C62828] hover:underline"><Trash2 className="w-4 h-4" /> Supprimer</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "trash" && (
        <div className={card}>
          <div className="flex items-center gap-2 mb-4">
            <Button data-testid="archive-back-trash" variant="outline" onClick={() => { setView("home"); loadOverview(); }} className="h-9"><ArrowLeft className="w-4 h-4 mr-1" /> Dossiers</Button>
            <div className="font-black text-lg flex-1">Corbeille ({trash.length})</div>
            <Button variant="outline" className="h-9" onClick={openTrash}><RefreshCw className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-2">
            {trash.length === 0 && <div className="py-6 text-center text-[#75807A]">La corbeille est vide.</div>}
            {trash.map((t) => (
              <div key={t.id} data-testid={`trash-item-${t.id}`} className="flex flex-wrap items-center gap-2 border border-[#ECE7DE] rounded-xl p-3">
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-[#F2EEE7] text-[#75807A]">{t.label}</span>
                <div className="flex-1 min-w-[160px]">
                  <div className="text-sm truncate">{t.summary || "(élément)"}</div>
                  <div className="text-xs text-[#75807A]">Supprimé le {fmtDate(t.deleted_at)}</div>
                </div>
                <button data-testid={`trash-restore-${t.id}`} onClick={() => restoreTrash(t)} className="inline-flex items-center gap-1 text-sm text-[#0B7A3B] hover:underline"><RotateCcw className="w-4 h-4" /> Restaurer</button>
                <button data-testid={`trash-purge-${t.id}`} onClick={() => purgeTrash(t)} className="inline-flex items-center gap-1 text-sm text-[#C62828] hover:underline"><Trash2 className="w-4 h-4" /> Supprimer définitivement</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
